import * as React from 'react';
import { useState, useCallback, memo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@restworld/feature-authentication';
import { cn, ModalWrapperCloseIcon, openInNewTab, useModal } from '@restworld/utils-common';

import { Button, FormLabelWrapper, TextInput, BaseModal } from '@restworld/ui-components';
import { Heading, Text } from '@restworld/ui-ds-base';
import { BookmarkIcon } from '@heroicons/react/24/outline';

export interface JobFavoritePositionButtonProps {
  favoriteClickHandler: (email?: string) => Promise<void>;
  isJobPositionFavorited?: boolean;
}
export const JobFavoritePositionButton = memo(
  ({ favoriteClickHandler, isJobPositionFavorited }: JobFavoritePositionButtonProps) => {
    const { t: jobPositionTranslate } = useTranslation('job-position-page');
    const { t: commonTranslate } = useTranslation('common');
    const { show, close, open } = useModal();
    const { isAuthenticated } = useAuth();
    const handleClick = useCallback(
      async (email?: string) => {
        await favoriteClickHandler(email);
        close();
      },
      [close, favoriteClickHandler]
    );
    const clickHandler = useCallback(() => {
      open();
      if (isAuthenticated) favoriteClickHandler();
    }, [favoriteClickHandler, isAuthenticated, open]);
    return (
      <>
        <Button
          variant="borderless"
          icon={!isJobPositionFavorited ? 'BookmarkIcon' : 'BookmarkSolidIcon'}
          onClick={clickHandler}
        />
        <BaseModal
          open={show}
          onClose={close}
          modalStyles={['w-full md:max-w-[26rem] md:min-w-[22rem]']}
          closeIcon={<ModalWrapperCloseIcon />}
          showMobileGestures
          title={
            <span className="flex items-center justify-center gap-1">
              <BookmarkIcon className="h-6 w-6" />
              <Heading size={4} weight="semibold" align="center">
                {commonTranslate('check_your_email')}
              </Heading>
            </span>
          }
        >
          <Wrapper>
            {isAuthenticated ? (
              <>
                <Text size={2} color="muted">
                  {jobPositionTranslate('job_favourite_email_information')}
                </Text>
                <Text size={2} color="muted">
                  {jobPositionTranslate('apply_at_any_time')}
                </Text>
              </>
            ) : (
              <JobFavoriteInputModal favoriteClickHandler={handleClick} />
            )}
          </Wrapper>
        </BaseModal>
      </>
    );
  }
);
export default JobFavoritePositionButton;
export const JobFavoriteInputModal = memo(
  ({ favoriteClickHandler }: { favoriteClickHandler: (email?: string) => Promise<void> }) => {
    const { t } = useTranslation('common');

    const [email, setEmail] = useState('');
    const formSubmitHandler = useCallback(
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        await favoriteClickHandler(email);
        setEmail('');
      },
      [email, favoriteClickHandler]
    );
    return (
      <>
        <Text>{t('input_email_request')}</Text>
        <FormStyled action="" onSubmit={formSubmitHandler}>
          <FormLabelWrapper label="Email" id="email">
            <TextInput
              id="email"
              onChange={(value) => setEmail(value.trim())}
              type="email"
              placeholder="sam@example.com"
              required
              value={email}
            />
          </FormLabelWrapper>
          <Button fullwidth type="submit">
            {t('submit_location')}
          </Button>
        </FormStyled>
        <LoginRedirect>
          <Heading as="span" size={5} weight="normal" color="muted" className="font-light">
            {t('have_an_account_already')}
          </Heading>
          <Button
            variant="ghost"
            onClick={() => openInNewTab(`/login?redirectTo=${encodeURIComponent(window.location.pathname)}`)}
          >
            Log in
          </Button>
        </LoginRedirect>
      </>
    );
  }
);
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4', className)} {...props} />
);
const LoginRedirect = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  // items-center so the "Hai gia un account?" label and the "Log in" button sit on one baseline
  <span className={cn('flex gap-1 self-center items-center', className)} {...props} />
);
const FormStyled = ({ className, ...props }: React.FormHTMLAttributes<HTMLFormElement>) => (
  <form className={cn('flex flex-col gap-4', className)} {...props} />
);
